<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import MinimalFooter from "~/layouts/components/MinimalFooter.vue";
import ScrollToTop from "~/components/utils/ScrollToTop.vue";
import CookieConsentContainer from "~/components/utils/CookieConsentContainer.vue";
import MinimalHeader from "~/layouts/components/MinimalHeader.vue";
import UploadDocument from "~/components/utils/UploadDocument.vue";
import {useDocumentStore} from "~/plugins/store/document";
import type {OcrResponse, TranslationList} from "~/types/Utils";
import {translation, useTranslationStore} from '~/plugins/store/translations';
import type {RcaInsuranceCompany} from "~/types/Rca";
import {useRcaStore} from "~/plugins/store/rca";
import {usePageStore} from "~/plugins/store/page";
import type {FaqCategory, FaqQuestion} from "~/types/Page";
import {useNuxtApp} from "#app";
import RegistrationPlate from "~/components/vehicle/RegistrationPlate.vue";

export default defineComponent({
  components: {RegistrationPlate, UploadDocument, MinimalHeader, CookieConsentContainer, ScrollToTop, MinimalFooter},
  setup() {
    useHead({
      title: 'Xiga | Life is Hard',
      meta: [
        {
          name: 'description',
          content: 'meta'
        }
      ],
    });

    const documentStore = useDocumentStore();
    const documentList = ref(documentStore.documentList);
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const rcaStore = useRcaStore();
    const rcaInsuranceCompanies = ref<RcaInsuranceCompany[]>([]);
    const pageStore = usePageStore();
    const faqCategories = ref<FaqCategory[] | null>([]);
    const faqQuestions = ref<FaqQuestion[] | null>([]);
    const faqTab = ref("0");
    const {$toast} = useNuxtApp();
    const registrationPlate = ref<string | null>(null);
    const isRegistrationPlateValid = ref(false);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');

      rcaInsuranceCompanies.value = await rcaStore.getAllRcaInsuranceCompanies();

      const fetchedFaqCategories = await pageStore.getFaqCategories();
      faqCategories.value = fetchedFaqCategories?.filter(c => c.code?.toLowerCase() === 'road_assistance') ?? null;

      faqQuestions.value = await pageStore.getQuestions();
    });

    const handleRegistrationPlateSubmit = () => {
      if (isRegistrationPlateValid.value) {
        navigateTo('/rca/' + registrationPlate.value + '?partner_code=lifeishard');
      } else {
        $toast.error(translation(translationList.value.registration_plate_invalid, "Număr de înmatriculare invalid"));
      }
    }

    const handleRegistrationPlateValidation = ({field, isValid}: { field: string, isValid: boolean }) => {
      if (field === 'registration_plate') {
        isRegistrationPlateValid.value = isValid;
      }
    }

    const filteredQuestions = (categoryId: number) => {
      if (faqQuestions.value) {
        return faqQuestions.value.filter(q => q.category_id === categoryId);
      }
      return [];
    };

    return {
      documentList,
      translationList,
      translation,
      handleRegistrationPlateSubmit,
      rcaInsuranceCompanies,
      faqCategories,
      faqQuestions,
      faqTab,
      filteredQuestions,
      registrationPlate,
      handleRegistrationPlateValidation
    }
  }
  });
</script>

<template>
  <div style="min-width: 350px;" class="fill-height d-flex flex-column">
    <div class="fill-height">
      <div class="d-flex justify-center py-2 border-1"  style="background-color: #37B44B;" >
        <a href="/">
          <img src="/images/logo-white.webp" alt="Xiga" style="width: 100px; max-width: 100%"/>
        </a>
      </div>
      <div class="custom-header-instant" style="margin-top: -1px;">
        <v-container fluid class="component-container-960mw">
          <v-row class="d-flex justify-center">
            <v-col cols="12">
              <v-card
                  class="mx-auto"
                  rounded="xl"
                  variant="elevated"
                  elevation="15"
              >
                <template v-slot:text>
                  <v-row class="d-flex align-center justify-center">
                    <v-col cols="12" sm="10" md="6" lg="6" xl="5" class="d-flex">
                      <NuxtImg src="/images/logo-lih-dark.png"
                               class="w-50"
                               alt="Life is hard"/>
                      <NuxtImg src="/images/logo-pointer.png"
                               class="w-50"
                               alt="Pointer systems"/>
                    </v-col>
                  </v-row>

                  <h1 class="text-center">
                    {{ translation(translationList.lih_partner_subtitle, "Ești pe mâini sigure alături de XIGA") }}
                  </h1>

                  <v-row class="d-flex justify-center">
                    <v-col cols="12" sm="10">
                      <div class="text-center fs-14px text-dark my-3">
                        {{ translation(translationList.lih_partner_description, "Pentru fiecare RCA de 12 luni achiziționat prin aplicația Life is Hard, primești gratuit un pachet de asistență rutieră de la partenerul Pointer Systems!") }}
                        <span class="fs-16px">*</span>
                      </div>
                    </v-col>
                  </v-row>

                  <div class="fw-700 text-center fs-14 font-italic">
                    <sup>*</sup>{{ translation(translationList.lih_partner_offer, "Ofertă valabilă doar pentru autoturisme.") }}
                  </div>

                  <v-row class="my-3">
                    <v-col cols="6" sm="6" md="3" class="d-flex flex-column align-center">
                      <NuxtImg src="/images/icons/tow-truck.png" alt="Tow truck" class="w-50" />
                      <span class="text-center font-weight-bold">
                        {{ translation(translationList.tow_truck, "Tractare 100 km") }}
                      </span>
                    </v-col>
                    <v-col cols="6" sm="6" md="3" class="d-flex flex-column align-center">
                      <NuxtImg src="/images/icons/mechanic.png" alt="Mecanic" class="w-50" />
                      <span class="text-center font-weight-bold">
                        {{ translation(translationList.mobile_workshop, "Atelier mobil") }}
                      </span>
                    </v-col>
                    <v-col cols="6" sm="6" md="3" class="d-flex flex-column align-center">
                      <NuxtImg src="/images/icons/pin-on-map.png" alt="Hartă" class="w-50 d-flex justify-center" />
                      <span class="text-center font-weight-bold">
                        {{ translation(translationList.territorial_coverage, "Acoperire teritorială") }}
                      </span>
                    </v-col>
                    <v-col cols="6" sm="6" md="3" class="d-flex flex-column align-center">
                      <NuxtImg src="/images/icons/call-center.png" alt="Call center" class="w-50 d-flex justify-center" />
                      <span class="text-center font-weight-bold">
                        {{ translation(translationList.call_center, "Suport telefonic") }}
                      </span>
                    </v-col>
                  </v-row>
                </template>

                <v-card-text class="bg-grey-lighten-4 pt-10">
                  <v-row class="d-flex justify-center align-center">
                    <v-col cols="12" sm="8" md="8">
                      <registration-plate
                        v-model="registrationPlate"
                        @validation-vehicle-status="handleRegistrationPlateValidation"
                      >
                      </registration-plate>
                      <v-btn
                        class="block-on-mobile w-100"
                        rounded="lg"
                        size="x-large"
                        @click="handleRegistrationPlateSubmit"
                        color="primary">
                        {{ translation(translationList.continue, "Continuă") }}
                      </v-btn>
                      <div class="text-dark text-center mt-2" style="font-size: 12px">
                        Continuând procesul de scanare, îți dai acordul pentru <a href="/info/terms-and-conditions" target="_blank">prelucrarea
                        datelor cu caracter personal</a> în scopul obținerii de oferte RCA
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- FAQ container -->
      <v-container v-if="faqCategories && faqCategories.length > 0" fluid class="component-container-full">
        <v-container class="container">
          <v-container class="component-container-800mw" style="min-height: 350px;">
            <div class="d-flex flex-column align-center justify-center">
              <div class="fw-700 fs-30 text-center">
                {{ translation(translationList.faq_title, "Întrebări frecvente") }}
              </div>
            </div>
            <div class="mt-10">
              <v-tabs
                  class="faq-tab"
                  v-model="faqTab"
                  fixed-tabs
                  center-active
                  show-arrows
              >
                <v-tab variant="outlined" class="fw-600 text-dark" selected-class="selected" rounded="xl"
                       v-for="(faqCategory, index) in faqCategories" :key="index">
                  {{ faqCategory.category }}
                </v-tab>
              </v-tabs>

              <v-tabs-window v-model="faqTab">
                <v-tabs-window-item
                    v-for="(faqCategory, index) in faqCategories"
                    :key="index"
                    :value="index"
                >
                  <v-expansion-panels class="my-5 border-solid-contrast">
                    <v-expansion-panel
                        bg-color="gray-light"
                        rounded="xl"
                        v-for="(faqQuestion, qIndex) in filteredQuestions(<number>faqCategory.id)"
                        :key="qIndex"
                    >
                      <template v-slot:title>
                        <div class="fw-700 fs-20">{{ faqQuestion.question }}</div>
                      </template>
                      <template v-slot:text>
                        <div class="fw-500 fs-16 mx-4">
                          {{ faqQuestion.answer }}
                        </div>
                      </template>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </v-tabs-window-item>
              </v-tabs-window>
            </div>
          </v-container>
        </v-container>
      </v-container>

      <!-- RCA Insurance Companies -->
      <v-container fluid class="component-container-full background-default">
        <v-container class="container">
          <div class="d-flex flex-row align-center justify-center my-5">
            <div class="fw-700 fs-30 mx-3 color-primary text-center">{{
                translation(translationList.partner_insurance_companies, "Companii de Asigurare Partenere")
              }}
            </div>
          </div>
          <v-row class="d-flex align-center justify-center mt-5">
            <div class="d-flex flex-wrap align-center justify-center pa-1">
              <v-col cols="auto" sm="auto" md="auto" class="pa-1"
                     v-for="(insuranceCompany, index) in rcaInsuranceCompanies"
                     :key="index">
                <v-card height="80" width="130" rounded="lg" flat color="ghost-white"
                        class="d-flex align-center justify-center">
                  <NuxtImg :src="'/images/companies/insurance/thumbnails/' + insuranceCompany.code + '.webp'"
                           :alt="insuranceCompany.name" class="pa-1 w-100 pulse animated"/>
                </v-card>
              </v-col>
            </div>
          </v-row>
        </v-container>
      </v-container>
    </div>
    <scroll-to-top></scroll-to-top>
    <cookie-consent-container/>
    <minimal-footer/>
  </div>
</template>

<style scoped>
.bg-lightgray {
  background-color: #EBEBEB;
}

.banner-size {
  max-width: 100%;
}

@media (max-width: 576px) {
  .talon-size {
    max-width: 80%;
  }
}

/* Adjust max-width for medium-sized screens */
@media (min-width: 576px) {
  .banner-size {
    max-width: 80%;
  }

  .talon-size {
    max-width: 80%;
  }
}

/* Adjust max-width for larger screens */
@media (min-width: 768px) {
  .banner-size {
    max-width: 50%;
  }

  .talon-size {
    max-width: 50%;
  }
}

@media (min-width: 1200px) {
  .banner-size {
    max-width: 35%;
  }

  .talon-size {
    max-width: 50%;
  }
}

.partner-max-height {
  min-height: 150px;
  max-height: 155px;
  padding-left: 5px;
  padding-right: 5px;
  margin-bottom: 5px;
  margin-top: 5px;
}

.partner-image-max-width {
  max-width: 200px;
}

@media (max-width: 575px) {
  .partner-max-height {
    min-height: 110px;
    max-height: 115px;
    padding-left: 6px !important;
    padding-right: 6px !important;
    margin-bottom: 6px !important;
    margin-top: 6px !important;
  }

  .partner-image-max-width {
    max-width: 130px;
  }
}
.circle {
  width: 40px;
  height: 40px;
  background-color: #37B44B;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-header-instant {
  position: relative;
  background-image: url('/images/misc/wave-instant.png');
  background-size: cover; /* Scale the image to fit the container */
  background-repeat: no-repeat; /* Prevent the image from repeating */
  color: #fff;
  padding: 20px;
}

@media (max-width: 767px) {
  .custom-header-instant {
    position: relative;
    background-image: url('/images/misc/wave-instant-mobile.png');
    background-size: cover;
    color: #fff; /* Text color for the footer */
    padding: 10px;
  }
}
</style>
